import axios from "axios";
import { userStore } from "../stores";

class EndpointError extends Error {
  constructor(obj, msg) {
    super();
    this.name = "EndpointError";
    this.message = obj;
    console.error("EndpointError detail", obj, msg);
    if (typeof Error.captureStackTrace === "function") {
      Error.captureStackTrace(this, this.constructor);
    } else {
      this.stack = new Error(obj).stack;
    }
  }
}

class Fetcher {
  constructor(apis, endpoints) {
    this.apis = apis;
    this.defaultApi = Object.values(apis)[0];
    this.endpoints = endpoints;
  }

  apis = {};
  defaultApi = undefined;
  endpoints = {};
  method = "GET";
  body = null;

  customOptions = {};

  get proxy() {
    const that = this;
    return new Proxy(that.endpoints, {
      get(target, prop) {
        if (!target[prop])
          throw new EndpointError("There isn't such endpoint", target[prop]);
        return new Proxy(target[prop], {
          get(subTarget, subProp) {
            if (!subTarget[subProp])
              throw new EndpointError(
                "There isn't such endpoint",
                target[prop]
              );
            return (...params) => {
              const all = [...params];
              return that.call(that.endpoints[prop][subProp](...all));
            };
          },
        });
      },
    });
  }

  get from() {
    return this.proxy;
  }
  get to() {
    return this.proxy;
  }

  call = async (endpoint) => {
    try {
      if (!this.defaultApi) return false;

      const url = `${this.defaultApi}/${endpoint}`.replace(
        /([^:]\/)\/+/g,
        "$1"
      );
      const body = this.body ? { data: this.body } : {};
      const options = {
        method: this.method,
        ...body,

        ...this.customOptions,
      };
      return await this.requestCall(url, options);
    } catch (e) {
      console.log("error", e);
      return e;
    }
  };

  requestCall = async (url, options) => {
    try {
      const res = await axios({
        url: url,
        ...options,
      });
      return res;
    } catch (error) {
      console.log("error", error);
      if (error?.response?.status === 401) {
        userStore.logout();
      }
      return error.response;
    }
  };

  setOptions = (customOptions) => {
    this.customOptions = customOptions;
    return this;
  };

  get get() {
    this.method = "GET";
    this.body = null;
    return this;
  }

  post = (body) => {
    this.method = "POST";
    this.body = body || {};
    return this;
  };

  put = (body) => {
    this.method = "PUT";
    this.body = body || {};
    return this;
  };

  get delete() {
    this.method = "DELETE";
    return this;
  }
}

const API_BASE_URL = window.env.REACT_APP_API;

const apis = {
  ip: `${API_BASE_URL}/`,
};

const endpoints = {
  tenants: {
    login_admin: () => `/tenants/login_admin`,
    login_tenant: () => `/tenants/login_tenant`,
    login_tenant_new: () => `/tenants/login_tenant_new`,
    send_OTP: () => `/auth/send_OTP`,
    verify_OTP: () => `/auth/verify_OTP`,
    get_sale_contracts: () => "/tenants/get_sale_contracts",
    get_sale_contract: () => "/tenants/get_sale_contract",
    get_sale_contract_docs: () => "/tenants/get_sale_contract_docs",
    get_sale_contract_messages: () => "/tenants/get_sale_contract_messages",
    get_site_sale_contracts: () => "/tenants/get_site_sale_contracts",
    update_customer_users: () => "/tenants/update_customer_users",
    get_cust_data: () => "/tenants/get_cust_data",
    update_cust_site: () => "/tenants/update_cust_site",
    update_cust_site_space: () => "/tenants/update_cust_site_space",
    update_cust_tenant: () => "/tenants/update_cust_tenant",
    update_site: () => "/tenants/update_site",
    update_tenant: () => "tenants/update_tenant",
    get_customer_sites: () => "/tenants/get_customer_sites",
    get_project_img: () => "/tenants/get_project_img",
    send_reset_password: () => "/tenants/send_reset_password",
    send_license_update_email: () => "/tenants/send_license_update_email",
    send_form_email: () => "/auth/send_form_email",
    reset_password: () => "/tenants/reset_password",
    set_admin_password: () => "/tenants/set_admin_password",
    verify_admin_password: () => "/tenants/verify_admin_password",
    check_admin_password: () => "/tenants/check_admin_password",
    update_message: () => "/tenants/update_message",
    get_encrypted_user: () => "/auth/get_encrypted_user",
    update_note: () => "/tenants/update_note",
    get_tenants_in_portal: () => "/tenants/get_tenants_in_portal",
    get_portal_user_data: () => "/tenants/get_portal_user_data",
    get_sale_contract_note: () => "/tenants/get_sale_contract_note",
    add_sale_contract_note: () => "/tenants/add_sale_contract_note",
    get_departments: () => "/tenants/get_departments",
    get_note_topics: () => "/tenants/get_note_topics",
    get_note_statuses: () => "/tenants/get_note_statuses",
    get_sale_contract_applies: () => "/tenants/get_sale_contract_applies",
    get_bedek_site_spaces: () => "/tenants/get_bedek_site_spaces",
    get_application_attributes: () => "/common/get_application_attributes",

  },
  // "Choosing Tenants" feature — ERP (Priority) proxied by the coral backend
  // (routes/teanats_choose.js). NOTE: coral app.js currently mounts this at
  // "/choose_tenants" — keep the backend mount and this prefix in sync.
  choose: {
    get_tenant_space: () => "/tenants_choose/get_tenant_space",
    add_signed_edqout: () => "/tenants_choose/add_signed_edqout",
    signed_edqout: () => "/tenants_choose/signed_edqout",
    tenantchange_signdoc: () => "/tenants_choose/tenantchange_signdoc",
    add_tenantchange_signdoc: () =>
      "/tenants_choose/add_tenantchange_signdoc",
    get_apartment_plans: () => "/tenants_choose/get_tenant_apartment_plans",
    add_apartment_plan: () => "/tenants_choose/add_tenant_apartment_plan",
    update_apartment_plan: () =>
      "/tenants_choose/update_tenant_apartment_plan",
    get_apartment_plan_status: () =>
      "/tenants_choose/get_apartment_plan_status",
    get_selection_types: () => "/tenants_choose/get_tenant_selection_types",
    get_selections: () => "/tenants_choose/get_tenant_selections",
    get_changes: () => "/tenants_choose/get_tenant_changes",
    add_change: () => "/tenants_choose/add_tenant_changes",
    update_change: () => "/tenants_choose/update_tenant_changes",
    add_selection: () => "/tenants_choose/add_tenant_selections",
    update_selection: () => "/tenants_choose/update_tenant_selections",
    get_selection_statuses: () => "/tenants_choose/get_selection_statuses",
    get_active_change_profile: () =>
      "/tenants_choose/get_active_change_profile",
    get_cprof_signed_status: () =>
      "/tenants_choose/get_cprof_signed_status",
    update_change_profile: () => "/tenants_choose/update_change_profile",
    get_supplier_selection: () => "/tenants_choose/get_tenant_sup_selection",
    get_models: () => "/tenants_choose/get_models",
  },
  repairPages: {
    get_repair_pages: () => "/repair_pages/get_repair_pages",
    get_repair_page: () => "/repair_pages/get_repair_page",
    get_site_areas: () => "/repair_pages/get_site_areas",
    get_locations: () => "/repair_pages/get_locations",
    get_domains: () => "/repair_pages/get_domains",
    add_repair_page: () => "/repair_pages/add_repair_page",
    update_repair_page: () => "repair_pages/update_repair_page",
    get_repair_page_statuses: () => "/repair_pages/get_repair_page_statuses",
    get_examin_user_data: () => "/repair_pages/get_examin_user_data",
    get_scheduled_data: () => "/repair_pages/get_scheduled_data",
    add_conversation_message: () => "/repair_pages/add_conversation_message",
    update_conversation_message: () => "/repair_pages/update_conversation_message",
    get_conversation_messages: () => "/repair_pages/get_conversation_messages",
    update_messages_as_read: () => "/repair_pages/update_messages_as_read",

  },
  common: {
    get_application_attributes: () => `/common/get_application_attributes`,

  },
};

const fetcher = () => {
  const sessionStorageUser = JSON.parse(
    window.sessionStorage.getItem("coralis-tenants-user")
  );
  const token = sessionStorageUser?.userDetails?.token;

  return new Fetcher(apis, endpoints).setOptions({
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
      "Content-Type": "application/json; charset=utf-8",
    },
  });
};

export default fetcher;
